import { z } from "zod";
import { RomPatch } from "./session.js";
import { GameVersion } from "./version.js";
import { U64String } from "./ids.js";

export const ApiError = z.object({
  code: z.number().int(),
  reason: z.string(), // e.g. "sync_pending", "superseded", "kind_mismatch", "active_session"
  message: z.string().optional(),
  retryAfterMs: z.number().int().optional(),
  expectedSeq: z.number().int().optional(),
});
export type ApiError = z.infer<typeof ApiError>;

export const PayFeeIn = z.enum(["RCD", "SOL"]);

// ---- auth (§20.1)
export const AuthNonceRequest = z.object({ wallet: z.string() });
export const AuthNonceResponse = z.object({
  nonce: z.string(),
  domain: z.string(),
  statement: z.string(),
  issuedAt: z.string(),
  expirationTime: z.string(),
  /** Absent on localnet: wallets check it against their own network and have none for localnet. */
  chainId: z.string().optional(),
});
/** Standard base64 (RFC 4648 §4, with padding). Wallet Standard returns raw bytes; clients encode them with this. */
export const Base64 = z.string().regex(/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/, "must be base64");
/**
 * Wallet Standard `solana:signIn` output. `account.publicKey` (32 bytes), `signedMessage` (the
 * exact UTF-8 SIWS message bytes) and `signature` (64 bytes) are all base64; base58 is rejected.
 */
export const AuthVerifyRequest = z.object({
  signInInput: z.record(z.unknown()),
  signInOutput: z.object({
    account: z.object({ address: z.string(), publicKey: Base64 }),
    signedMessage: Base64,
    signature: Base64,
  }),
});
export type AuthVerifyRequest = z.infer<typeof AuthVerifyRequest>;
export const AuthResponse = z.object({
  accessToken: z.string(),
  player: z.object({ registered: z.boolean() }),
  wallet: z.string().optional(),
});
export type AuthResponse = z.infer<typeof AuthResponse>;

// ---- players (§20.2)
export const RegisterRequest = z.object({ initialVaultLamports: U64String });
export const BuiltTransactionResponse = z.object({
  transaction: z.string(), // base64 wire transaction
  feeRcd: z.number().int().optional(),
  netAmount: z.number().int().optional(),
  depositLamports: U64String.optional(),
  refundLamports: U64String.optional(),
  refundAs: z.enum(["rcd", "sol"]).optional(),
  refundRcd: U64String.nullable().optional(),
  suggestedVaultLamports: U64String.optional(),
});
export type BuiltTransactionResponse = z.infer<typeof BuiltTransactionResponse>;
export const SubmitSignedRequest = z.object({ signedTransaction: z.string() });
export const SignatureResponse = z.object({ signature: z.string(), migrationId: z.string().optional() });

export const PlayerMe = z.object({
  /** `players.id`; null before the off-chain row exists. Link HELLO carries it. */
  playerId: z.string().uuid().nullable(),
  wallet: z.string(),
  walletKind: z.enum(["external", "custodial"]),
  registered: z.boolean(),
  trainerName: z.string().nullable(),
  trainerId: z.number().int().nullable(),
  gameVersion: GameVersion.nullable(),
  saveSeq: z.number().int(),
  mintGatePassed: z.boolean(),
  /** Badges required before the first mint. 0 means the gate is open. */
  mintGateBadges: z.number().int().optional(),
  gameBalance: U64String,
  moneyOffset: z.string().regex(/^-?\d{1,20}$/), // i64
  feeDebtRcd: U64String,
  walletRcd: U64String,
  /** §15.5.6: program-owned ₽ address; anything sent here is credited in game at the next save. */
  depositAddress: z.object({ address: z.string(), pendingRcd: U64String }),
  /**
   * §15.5.5: the spending limit the wallet approved for the game (Token-2022 delegate = the deposit PDA).
   * Saves pull what waiting mints need from the wallet, up to `usable` (min of allowance and wallet ₽).
   */
  walletAllowance: z.object({ approved: U64String, usable: U64String }),
  claim: z.object({ remainingToday: U64String, globalRemainingToday: U64String, paused: z.boolean() }),
  fees: z.object({
    perSave: z.number(),
    perAutosave: z.number(),
    perMint: z.number(),
    perRelayedTx: z.number(),
    /** Whether network fees can be paid in ₽ through the Kora relay. */
    relayAvailable: z.boolean(),
  }),
  monCount: z.number().int(),
  lastSyncedAt: z.string().nullable(),
  status: z.string(),
  syncPending: z.boolean(),
  activeSession: z.boolean(),
  vault: z.object({
    address: z.string(),
    lamports: U64String,
    minimum: U64String,
    pendingMints: z.number().int(),
    pendingItemBag: z.boolean(),
    topUpNeeded: U64String,
    /** §15.5.5: ₽ still needed for the waiting mints after the in-game balance, deposit address and wallet allowance. */
    mintRcdNeeded: U64String,
    rentPoolShort: z.boolean(),
    refundableLockedLamports: U64String,
  }),
  /** §15.5.5: how release refunds are paid (burns choose per burn). */
  locks: z.array(z.object({
    action: z.string(),
    locked: z.boolean(),
    reason: z.string(),
    requirement: z.string(),
    progress: z.string().optional(),
  })),
  refunds: z.object({
    releaseAs: z.enum(["rcd", "sol"]),
    /** ₽ credited per 1 SOL refunded; null when refunds as ₽ are off. */
    rcdPerSol: U64String.nullable(),
  }),
});
export type PlayerMe = z.infer<typeof PlayerMe>;
export const RefundAs = z.enum(["rcd", "sol"]);
export type RefundAs = z.infer<typeof RefundAs>;
export const RefundPrefRequest = z.object({ releaseAs: RefundAs });

export const VaultDepositRequest = z.object({ lamports: U64String });
export const VaultWithdrawRequest = z.object({ lamports: U64String, payFeeIn: PayFeeIn });
export const RcdClaimRequest = z.object({ amount: z.number().int().positive(), confirmMidSession: z.boolean().optional() });
export const RcdDepositRequest = z.object({ amount: z.number().int().positive(), payFeeIn: PayFeeIn });
/** 0 revokes the allowance. */
export const RcdAllowanceRequest = z.object({ amount: z.number().int().min(0).max(1_000_000_000), payFeeIn: PayFeeIn });
export const ItemClaimRequest = z.object({ index: z.number().int().min(0).max(9), amount: z.number().int().positive(), payFeeIn: PayFeeIn });
export const ItemDepositRequest = ItemClaimRequest.extend({ asset: z.string().optional() });

export const ItemsResponse = z.object({
  items: z.array(
    z.object({
      index: z.number().int(),
      name: z.string(),
      inGame: z.number().int(),
      debt: z.number().int(),
      credit: z.number().int(),
      foundThisGeneration: z.number().int(),
      cap: z.number().int(),
      wallet: z.number().int(),
      waitingForSpace: z.number().int(),
    }),
  ),
});

export const RelayBuildKind = z.enum([
  "burn_mon",
  "rcd_transfer",
  "nft_transfer",
  "item_transfer",
  "withdraw_vault",
  "claim_item",
  "deposit_item",
  "claim_rcd",
  "deposit_rcd",
  "rcd_allowance",
  "wager",
  "close_player",
]);
export const RelayBuildRequest = z.object({ kind: RelayBuildKind, params: z.record(z.unknown()) });

export const NewGameRequest = z.object({ confirm: z.literal(true), gameVersion: GameVersion });
export const MigrateRequest = z.object({ newWallet: z.string(), newWalletProof: AuthVerifyRequest });
export const CloseRequest = z.object({ confirmForfeit: z.boolean() });
export const CloseResponse = BuiltTransactionResponse.extend({ blockers: z.array(z.string()) });

// ---- link and wagers (§20.5)
// Served by link-relay; the API proxies /v1/link/* there (and the WebSocket to link-relay or link-host by host mode).
export const LinkLobbyMode = z.enum(["trade", "battle", "wager_battle"]);
export const LinkHostMode = z.enum(["lockstep", "server"]);
export type LinkHostMode = z.infer<typeof LinkHostMode>;
export const LinkLobbyStatus = z.enum(["lobby", "handshake", "running", "ended", "aborted", "desynced"]);
export const LobbyCode = z.string().regex(/^[0-9a-f]{8}$/i);

/** POST /v1/link/lobbies. `wager_battle` and `serverRun` lobbies are hosted by link-host (§17.2.1). */
export const CreateLobbyRequest = z.object({ mode: LinkLobbyMode, serverRun: z.boolean().optional() });
export type CreateLobbyRequest = z.infer<typeof CreateLobbyRequest>;
export const CreateLobbyResponse = z.object({ lobbyId: z.string(), code: LobbyCode, hostMode: LinkHostMode });
export type CreateLobbyResponse = z.infer<typeof CreateLobbyResponse>;
/** POST /v1/link/lobbies/:code/join (empty body). `mode` is the stored mode: wagered lobbies read "battle". */
export const JoinLobbyResponse = z.object({ lobbyId: z.string(), hostMode: LinkHostMode, mode: z.enum(["battle", "trade"]) });
export type JoinLobbyResponse = z.infer<typeof JoinLobbyResponse>;
/** GET /v1/link/lobbies/:id (members only). */
export const LobbyStatusResponse = z.object({
  lobbyId: z.string(),
  code: LobbyCode,
  mode: z.enum(["battle", "trade"]),
  hostMode: LinkHostMode,
  status: LinkLobbyStatus,
  side: z.enum(["A", "B"]),
  opponentJoined: z.boolean(),
  inputDelay: z.number().int(),
  /** Both Cable Club states are verified and stored (`state_a_key`/`state_b_key`); the handshake can start. */
  statesReady: z.boolean(),
  /** The opponent's game version, so a lockstep client loads the right ROM for the opponent core. */
  opponentGameVersion: GameVersion.nullable(),
  /** The opponent's sessions.rom_patch. Null is retail. A different version from the caller refuses the link. */
  opponentRomPatch: RomPatch.nullable(),
});
export type LobbyStatusResponse = z.infer<typeof LobbyStatusResponse>;
/**
 * POST /v1/link/lobbies/:id/checkpoint (served by the API). Binds the caller's Cable Club checkpoint
 * (the forced save before linking) to its side of the lobby; once both are verified the API sets
 * `state_a_key`/`state_b_key` from the verifier's server states.
 */
export const BindLinkCheckpointRequest = z.object({ checkpointId: z.string().uuid() });
export const BindLinkCheckpointResponse = z.object({ side: z.enum(["A", "B"]), statesReady: z.boolean() });
export type BindLinkCheckpointResponse = z.infer<typeof BindLinkCheckpointResponse>;
/** POST /v1/link/lobbies/:id/leave. Aborts a lobby that has not started. */
export const LeaveLobbyResponse = z.object({ lobbyId: z.string(), status: z.literal("aborted") });
export type LeaveLobbyResponse = z.infer<typeof LeaveLobbyResponse>;

/** POST /v1/wagers. The opponent is the lobby's other player; on chain both sides are Player PDAs. */
export const CreateWagerRequest = z.object({ lobbyId: z.string(), stake: z.number().int().positive() });
export const CreateWagerResponse = z.object({ wagerId: z.string(), transaction: z.string() });
export type CreateWagerResponse = z.infer<typeof CreateWagerResponse>;

// ---- read endpoints (§20.2, §20.4)
const StatBlock = z.object({ hp: z.number().int(), attack: z.number().int(), defense: z.number().int(), speed: z.number().int(), special: z.number().int() });

/** One Pokémon from GET /v1/mons?owner=. Stat experience is `statExp` (Gen 1 term). */
export const MonSummary = z.object({
  asset: z.string(),
  monId: z.string().nullable(),
  nickname: z.string(),
  otName: z.string(),
  speciesIndex: z.number().int(),
  dex: z.number().int(),
  speciesName: z.string(),
  level: z.number().int(),
  exp: z.number().int(),
  hp: z.number().int(),
  /** Gen 1 status-condition byte from the box struct. */
  condition: z.number().int(),
  types: z.tuple([z.string(), z.string()]),
  moves: z.array(z.string()),
  otId: z.number().int(),
  dvs: StatBlock,
  statExp: StatBlock,
  stats: z.object({ maxHp: z.number().int(), attack: z.number().int(), defense: z.number().int(), speed: z.number().int(), special: z.number().int() }),
  growthRate: z.string(),
  originVersion: GameVersion,
  saveSeq: z.number().int(),
  /** `mons.status` ("active", "pending_release", ...). */
  status: z.string(),
  taint: z.string().nullable(),
  refundableLamports: U64String,
  /** ₽ a burn would credit instead of the SOL refund; null when refunds as ₽ are unavailable. */
  refundRcd: U64String.nullable(),
  /** False when the deposit was paid in ₽ from the rent pool: burn for ₽ only (§15.5.5). */
  solRefundAllowed: z.boolean(),
  metadata: z.string(),
  image: z.string(),
  page: z.string(),
});
export type MonSummary = z.infer<typeof MonSummary>;
export const MonsResponse = z.object({
  mons: z.array(MonSummary),
  /** ₽ per SOL for level prices (§5.9, `levelsPriceRcd`); "0" when levels cannot be bought. */
  rcdPerSol: U64String.default("0"),
});
export type MonsResponse = z.infer<typeof MonsResponse>;
export const BuyLevelsRequest = z.object({ targetLevel: z.number().int().min(2).max(100) });
export const BuyLevelsResponse = z.object({
  transaction: z.string(),
  fromLevel: z.number().int(),
  toLevel: z.number().int(),
  /** ₽ for the levels; storage growth of the NFT, if any, is charged on top. */
  rcd: U64String,
});
export type BuyLevelsResponse = z.infer<typeof BuyLevelsResponse>;
export const BurnMonRequest = z.object({ refundAs: RefundAs.default("rcd") });
export const BurnMonResponse = z.object({
  transaction: z.string(),
  refundLamports: U64String,
  refundAs: RefundAs,
  /** Set when `refundAs` is "rcd". */
  refundRcd: U64String.nullable(),
});

/** GET /v1/mon/:asset.json — Metaplex-style metadata (§15.9). */
export const MonMetadataResponse = z.object({
  name: z.string(),
  symbol: z.string(),
  description: z.string(),
  image: z.string(),
  external_url: z.string(),
  attributes: z.array(
    z.object({
      trait_type: z.string(),
      value: z.union([z.string(), z.number()]),
      display_type: z.literal("number").optional(),
      max_value: z.number().optional(),
    }),
  ),
  properties: z.object({
    category: z.string(),
    files: z.array(z.object({ uri: z.string(), type: z.string() })),
    refundable_lamports: z.number(),
  }),
});
export type MonMetadataResponse = z.infer<typeof MonMetadataResponse>;

/** GET /v1/token/stats. RCD amounts are whole ₽ as strings; `pool.price` is lamports per ₽1. */
export const TokenStatsResponse = z.object({
  supply: U64String,
  reserve: U64String,
  totalGameBalance: U64String,
  feesAccrued: U64String,
  treasury: U64String,
  pool: z.object({ rcd: U64String.nullable(), sol: U64String.nullable(), price: z.number().nullable() }),
  mintAuthority: z.string().nullable(),
  items: z.array(
    z.object({
      index: z.number().int(),
      name: z.string(),
      supply: U64String,
      inGameTotal: z.number().int(),
      masterBallsMinted: z.number().int().optional(),
    }),
  ),
});
export type TokenStatsResponse = z.infer<typeof TokenStatsResponse>;

const I64String = z.string().regex(/^-?\d{1,20}$/);
/** GET /v1/money/me. Only committed rows (no provisional `save_seq < 0`) of the current chain epoch. */
export const MoneyLedgerEntry = z.object({
  id: z.string(),
  saveSeq: z.number().int(),
  purseBefore: z.number().int(),
  purseAfter: z.number().int(),
  delta: I64String,
  credited: U64String,
  debited: U64String,
  feePaid: U64String,
  balanceAfter: U64String,
  offsetAfter: I64String,
  rareItemSale: U64String,
  signature: z.string().nullable(),
  at: z.string(),
});
export type MoneyLedgerEntry = z.infer<typeof MoneyLedgerEntry>;
export const MoneyMeResponse = z.object({
  gameBalance: U64String,
  offset: I64String,
  effective: z.string(),
  walletRcd: U64String,
  feeDebtRcd: U64String,
  ledger: z.array(MoneyLedgerEntry),
});
export type MoneyMeResponse = z.infer<typeof MoneyMeResponse>;

/** GET /v1/players/me/vault/ledger?before=&limit=. */
export const VaultLedgerEntry = z.object({
  id: z.string(),
  kind: z.enum([
    "deposit",
    "withdraw",
    "mint_deposit",
    "growth_deposit",
    "item_bag_deposit",
    "item_ata_deposit",
    "mball_deposit",
    "refund",
    "migration_out",
    "migration_in",
  ]),
  lamports: I64String,
  monId: z.string().nullable(),
  saveSeq: I64String.nullable(),
  signature: z.string(),
  slot: U64String,
  at: z.string(),
});
export type VaultLedgerEntry = z.infer<typeof VaultLedgerEntry>;
export const VaultLedgerResponse = z.object({ rows: z.array(VaultLedgerEntry) });
export type VaultLedgerResponse = z.infer<typeof VaultLedgerResponse>;

/** GET /v1/players/me/rcd/transfers?before=&limit=. */
export const RcdTransferEntry = z.object({
  id: z.string(),
  kind: z.enum(["claim", "deposit"]),
  amount: U64String,
  signature: z.string(),
  slot: U64String,
  at: z.string(),
});
export type RcdTransferEntry = z.infer<typeof RcdTransferEntry>;
export const RcdTransfersResponse = z.object({ rows: z.array(RcdTransferEntry) });
export type RcdTransfersResponse = z.infer<typeof RcdTransfersResponse>;
